import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {httpGetNotifications} from '../http/notifications.http'
import {QUERY_KEYS} from '@/queryClient'
import {NotificationsResponse} from '../types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

/**
 * Hook to fetch notifications with optional read status filter and pagination
 * @param toRead Optional filter for read/unread notifications
 * @param limit Number of results per page (defaults to 30)
 * @param options Additional query options
 */
export const useGetInfiniteNotifications = (
    toRead?: boolean,
    limit: number = 30,
    options?: Pick<UseInfiniteQueryOptions<NotificationsResponse>, 'enabled'>
) => {
    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.NOTIFICATIONS, {toRead, limit}],
        queryFn: ({pageParam = 1}) =>
            httpGetNotifications({
                params: {
                    ...(toRead !== undefined ? {toRead} : {}),
                    limit,
                    page: pageParam
                }
            }),
        initialPageParam: 1,
        ...options,
        getNextPageParam: lastPage => {
            const meta = lastPage.meta
            return meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null
        }
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        notifications: query.data?.pages ? query.data.pages.flatMap(page => page.data) : []
    }
}
